import styled, { css } from 'styled-components/macro';

import Box from 'components/elements/Box';
import P from 'components/elements/p';
import { SearchFieldContainer } from 'components/styled/SearchField/SearchField';
import { WhiteBlock } from 'components/utility/GenericComponent/GenericComponent';
import Cell from 'components/data-visualization/Table/Cell';
import { NoDataOverlay } from 'components/data-visualization/Table/Placeholder';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';

import { ReactComponent as Trashbin } from 'icons/icon-trashbin.svg';
import { ReactComponent as NotificationIcon } from 'icons/notification-icon.svg';

import { scrollBar } from 'styles/base-classes';

import { Label as TabsLabel } from 'components/data-containers/PositionsTable/TableControls/DSPToggle.style';
import { Circle } from 'components/base/KeyValuePair/KeyValuePair';

import { TableRow } from 'components/data-containers/BrowseEntities/TableRow/TableRow.style';

export const Container = styled(Box)`
  margin: 0.5rem;

  @media screen and (min-width: 47.0625rem) {
    width: calc(100% - 1em * 2);
    margin: 1em auto;
  }
  @media screen and (min-width: 50em) {
    width: 90%;
  }
  @media screen and (min-width: 60em) {
    width: 90%;
  }
  @media screen and (min-width: 80em) {
    width: 75%;
  }
`;

export const GreenButton = styled('button')`
  padding: 0.5rem 1.2rem;
  border-radius: 3px;
  background-color: ${({ theme }): string => theme.AccentGreen};
  color: ${({ theme }): string => theme.White};
  font-size: 1.125rem;
  font-weight: 700;
`;

export const TableWrapper = styled('div')`
  width: 100%;
`;

export const SearchDropdownBox = styled(Box)<{
  isSeparate?: boolean;
  isSmallScreen?: boolean;
}>`
  position: absolute;
  right: 0;
  z-index: 2;
  background-color: ${({ theme }): string => theme.White};
  border: 1px solid #f5f5f5;

  @media screen and (max-width: 40em) {
    position: relative;
  }

  ${SearchFieldContainer} {
    background-color: ${({ theme }): string => theme.White};
    padding: ${({ isSmallScreen }): string | number =>
      isSmallScreen ? '0.75rem 0' : '0 0 0.25rem'};

    &:after {
      width: 100%;
      height: 1px;
    }
  }

  ${({ isSeparate }): any =>
    isSeparate &&
    css`
      right: 50%;
      transform: translateX(50%);
    `}
`;

export const Label = styled(P)`
  padding-right: 1rem;
  color: ${({ theme }): string => theme.MediumGrey};
  font-size: 0.6875rem;
  text-transform: uppercase;
`;

export const ScrollBox = styled(Box)`
  ${scrollBar};
`;

export const Wrapper = styled(Box)`
  & > ${WhiteBlock} {
    @media screen and (min-width: 753px) and (max-width: 800px) {
      width: calc(100% - 1em * 2);
      margin: 1em auto;
    }
  }
`;

export const Button = styled('button')`
  padding-right: 2rem;
  flex-shrink: 0;
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }): string => theme.AccentGreen};
  &:hover {
    cursor: pointer;
  }
`;

export const TrashbinBox = styled(Trashbin)`
  path {
    fill: ${({ theme }): string => theme.NightBlue};
  }
`;

export const NotificationBox = styled(NotificationIcon)`
  path {
    fill: ${({ theme }): string => theme.NightBlue};
  }
`;

export const IconBox = styled(Box)`
  display: none;

  &:hover {
    background-color: #eaeaea;
  }
`;

export const RowCell = styled(Cell)<any>`
  width: 100%;
  display: flex;
  align-items: ${({ isSmallScreen }: { isSmallScreen: boolean }): string =>
    isSmallScreen ? 'flex-start' : 'center'};
  flex-direction: row;
  padding-right: ${({
    isSmallScreen,
    theme,
  }: {
    isSmallScreen: boolean;
    theme: any;
  }): string => (isSmallScreen ? '0' : theme.spacing.xsmall)};
  overflow: hidden;
  ${({ justifyContent }): string =>
    justifyContent ? `justify-content: ${justifyContent};` : ''}

  &:hover ${IconBox} {
    display: flex;
  }
  ${({ isExpanded }): any =>
    isExpanded &&
    css`
      &,
      ${AnimatedLoadingWrapper}, ${Box}, ${NoDataOverlay}, table,
      tr {
        background-color: #f7f7f7;
      }

      padding-top: 1.25rem;
      box-shadow: inset 0px 9px 10px -6px #d9d7d7;
    `}

  ${({ isSmallScreen }): any =>
    isSmallScreen &&
    css`
      padding-top: 0;
      padding-bottom: 0;
    `}
`;

export const TabsWrapper = styled(Box)<{ isSmallScreen?: boolean }>`
  ${TabsLabel} {
    width: ${({ isSmallScreen }): string => (isSmallScreen ? 'auto' : '244px')};
    max-width: auto;
    margin-right: ${({ isSmallScreen }): string =>
      isSmallScreen ? '0' : '0.625rem'};
  }
`;

export const CustomerNameElement = styled.div`
  font-size: 11px;
  color: ${({ theme }): string => theme.CasperGrey};
  text-align: center;
`;

export const PlaylistName = styled.h3`
  display: flex;
  -webkit-box-align: center;
  align-items: center;
  font-weight: 600;
  color: ${({ theme }): string => theme.DeepBlue};
  line-height: 1.4;
  margin: 0px;
  font-size: 1em;
  text-align: left;
`;

export const PlaylistOwner = styled.span`
  line-height: 1.4;
  color: ${({ theme }): string => theme.CasperGrey};
  text-align: left;
`;

export const TablePlaylistOwner = styled.span`
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }): string => theme.MediumGrey};
  text-align: left;
  font-weight: 600;
`;

export const PlaylistTableHead = styled.thead`
  th {
    font-size: 11px;
    text-transform: uppercase;
    color: ${({ theme }): string => theme.MediumGrey};
    line-height: 14px;
    font-weight: 600;
    letter-spacing: 1px;

    &:first-of-type {
      text-align: left;
    }

    &:last-of-type {
      width: 50px;
    }
  }
`;

export const PlaylistTableRowElement = styled.tr<any>`
  border-bottom: 1px solid ${({ theme }): string => theme.LightGrey};

  background: ${({ isHighlighted, theme }): string | false | undefined =>
    isHighlighted && `${theme.Sand}`};

  display: table-row;
  border-bottom: 0;

  td {
    border-bottom: 1px solid ${({ theme }): string => theme.LightGrey};
  }

  &:hover {
    background: ${({ theme }): string => theme.Sand};

    ${IconBox} {
      display: flex;
    }
  }

  ${({ isExpanded }): any =>
    isExpanded &&
    css`
      &,
      ${AnimatedLoadingWrapper}, ${Box}, ${NoDataOverlay}, table,
      tr {
        background-color: #f7f7f7;
      }

      box-shadow: inset 0px 9px 10px -6px #d9d7d7;
    `}
`;

export const ListItemExpanded = styled(Box)`
  border-bottom: 1px solid ${({ theme }): string => theme.LightGrey};

  background: ${({ theme }): string => theme.Sand};

  ${AnimatedLoadingWrapper}, ${Box}, ${NoDataOverlay} {
    background-color: #f7f7f7;
  }
`;

export const PlaylistTableDataCell = styled.td`
  font-size: 14px;
  font-family: 'Open Sans';
  font-weight: 400;
  line-height: 19.07px;
  color: black;
  text-align: center;
  width: 15%;
`;

export const PriorityCircle = styled(Circle)`
  margin-bottom: 0;
`;

export const Title = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  line-height: 26px;
  font-weight: 600;
  color: ${({ theme }): string => theme.MediumGrey};
`;

export const BoxItem = styled(Box)`
  display: flex;
  flex-direction: row;

  & > * {
    width: 50%;
  }
`;

export const ExtendedTextButton = styled(Button)<{ textAlign?: string }>`
  width: 100%;
  min-height: 3.125rem;
  margin-top: 0.5rem;
  color: ${({ theme }): string => theme.AccentGreen};
  padding-right: 0;
  text-align: ${({ textAlign }): string => textAlign || 'center'};

  &:hover {
    background-color: ${({ theme }): string => theme.LightGrey};
  }
`;

export const MobileIconBox = styled(IconBox)`
  display: flex;
`;

export const IconsRowCell = styled(RowCell)`
  width: 0;
  overflow: hidden;
  padding-right: 0;
  padding-left: 0;

  ${({ isWide }): any =>
    isWide &&
    css`
      width: calc(50px * 2 + 2px);
      transition: width 200ms linear;
    `}
`;

export const Row = styled(TableRow)<{ isSmallScreen?: boolean }>`
  ${({ isSmallScreen }): any =>
    isSmallScreen &&
    css`
      &:hover {
        background-color: transparent;
        ${ExtendedTextButton} {
          background-color: transparent;
        }
      }
    `}
`;

export const TransitionBox = styled(Box)`
  transition: all 200ms linear;
`;
